// src/components/profiles/ProfileCard.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Typography,
  Box,
  Stack,
  Button,
  Tooltip,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShareIcon from '@mui/icons-material/Share';
import VerifiedIcon from '@mui/icons-material/Verified';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { getZodiacSign, getZodiacColor } from '../../../utils/zodiacUtils';

const ProfileCard = ({ profile, priority = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const profileId = profile.id || profile.profile_id;
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Responsive dimensions
  const {
    cardHeight,
    imageWidth,
    imageHeight,
    imageContainerWidth,
    fontSizeMultiplier,
    paddingMultiplier
  } = useMemo(() => {
    let cardHeight = 250;
    let fontSizeMultiplier = 1;
    let paddingMultiplier = 1;
    
    if (isMobile) {
      cardHeight = 200;
      fontSizeMultiplier = 0.85;
      paddingMultiplier = 0.8;
    } else if (isTablet) {
      cardHeight = 230;
      fontSizeMultiplier = 0.95;
      paddingMultiplier = 0.9;
    }
    
    const imageAspectRatio = 3/4;
    const imagePaddingTopBottom = 8 * paddingMultiplier;
    const imagePaddingLeftRight = 4 * paddingMultiplier;
    
    const imageHeight = cardHeight - (imagePaddingTopBottom * 2);
    const imageWidth = imageHeight * imageAspectRatio;
    const imageContainerWidth = imageWidth + (imagePaddingLeftRight * 2);
    
    return {
      cardHeight,
      imageWidth,
      imageHeight,
      imageContainerWidth,
      fontSizeMultiplier,
      paddingMultiplier
    };
  }, [isMobile, isTablet]);

  // Memoized calculations
  const zodiac = useMemo(() => getZodiacSign(profile.date_of_birth), [profile.date_of_birth]);
  const zodiacColor = useMemo(() => getZodiacColor(zodiac.element), [zodiac.element]);

  // Determine gender from multiple sources
  const genderDisplay = useMemo(() => {
    const idGender = profileId?.charAt(0)?.toUpperCase();
    const profileGender = profile.gender?.toUpperCase();
    
    if (idGender === 'M' || idGender === 'F') {
      return idGender;
    } else if (profileGender === 'M' || profileGender === 'F' || 
               profileGender === 'MALE' || profileGender === 'FEMALE') {
      return profileGender.charAt(0);
    }
    
    return 'M';
  }, [profileId, profile.gender]);

  const isMale = genderDisplay === 'M';
  const isFemale = genderDisplay === 'F';

  // Get default image based on gender
  const getDefaultImage = useCallback(() => {
    if (isFemale) {
      return `/whiz-public/profiles-images/default-female.webp`;
    }
    return `/whiz-public/profiles-images/default-male.webp`;
  }, [isFemale]);

  // Generate profile image URL
  const getProfileImageUrl = useCallback(() => {
    if (!profileId) {
      return getDefaultImage();
    }
    return `/whiz-public/profiles-images/${profileId}.webp`;
  }, [profileId, getDefaultImage]);

  // Set default image
  const setDefaultImage = useCallback(() => {
    setImageUrl(getDefaultImage());
    setImageError(true);
    setImageLoaded(true);
  }, [getDefaultImage]);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    if (!cardRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          if (observerRef.current && cardRef.current) {
            observerRef.current.unobserve(cardRef.current);
          }
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observerRef.current.observe(cardRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Load image when card comes into view
  useEffect(() => {
    if (isInView && !imageLoaded && !imageError) {
      const profileImageUrl = getProfileImageUrl();
      setImageUrl(profileImageUrl);
    }
  }, [isInView, getProfileImageUrl, imageLoaded, imageError]);

  // Format income
  const formattedIncome = useMemo(() => {
    const income = profile.monthly_income;
    if (!income) return null;
    
    if (typeof income === 'string') {
      if (/^\d+$/.test(income.trim())) {
        const incomeNum = parseInt(income, 10);
        if (isNaN(incomeNum) || incomeNum === 0) return null;
        
        if (incomeNum >= 100000) {
          return `PKR ${(incomeNum / 100000).toFixed(1)} Lakh/mo`;
        } else if (incomeNum >= 1000) {
          return `PKR ${Math.round(incomeNum / 1000)}K/mo`;
        } else {
          return `PKR ${incomeNum}/mo`;
        }
      }
      
      const currencyMatch = income.match(/([^\d\s]+)?\s*([\d,.]+)\s*([^\d\s]+)?/);
      
      if (currencyMatch) {
        const prefixCurrency = currencyMatch[1] || '';
        const amountStr = currencyMatch[2].replace(/,/g, '');
        const suffixCurrency = currencyMatch[3] || '';
        const currency = (prefixCurrency || suffixCurrency).toUpperCase().trim();
        const amount = parseFloat(amountStr);
        
        if (isNaN(amount) || amount === 0) return null;
        
        let formattedAmount;
        if (amount >= 100000) {
          formattedAmount = `${(amount / 100000).toFixed(1)} Lakh`;
        } else if (amount >= 1000) {
          formattedAmount = `${Math.round(amount / 1000)}K`;
        } else {
          formattedAmount = amount.toString();
        }
        
        return `${currency} ${formattedAmount}/mo`;
      }
      
      return income;
    }
    
    if (typeof income === 'number') {
      if (income === 0) return null;
      
      if (income >= 100000) {
        return `PKR ${(income / 100000).toFixed(1)} Lakh/mo`;
      } else if (income >= 1000) {
        return `PKR ${Math.round(income / 1000)}K/mo`;
      } else {
        return `PKR ${income}/mo`;
      }
    }
    
    return null;
  }, [profile.monthly_income]);

  // Event handlers
  const handleShareClick = useCallback((e) => {
    e.stopPropagation();
    
    const profileUrl = `${window.location.origin}/profile/${profileId}`;
    const genderText = isMale ? 'Male' : 'Female';
    const message = `Check out ${profile.full_name} (${profile.age}y, ${genderText}) on Noor-un-Nikah: ${profileUrl}`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  }, [profile, profileId, isMale]);

  const handleViewClick = useCallback((e) => {
    e.stopPropagation();
    navigate(`/profile/${profileId}`);
  }, [navigate, profileId]);

  const handleCardClick = useCallback(() => {
    navigate(`/profile/${profileId}`);
  }, [navigate, profileId]);

  // Hover handlers
  const handleMouseEnter = useCallback(() => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Show hover state after a short delay
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Clear timeout if mouse leaves before hover state activates
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(false);
  }, []);

  // Handle image load error
  const handleImageError = useCallback(() => {
    if (!imageError) {
      setDefaultImage();
    }
  }, [imageError, setDefaultImage]);

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  // Determine final image URL to display
  const finalImageUrl = imageUrl || getDefaultImage();

  // Responsive font sizes
  const nameFontSize = 0.95 * fontSizeMultiplier;
  const captionFontSize = 0.85 * fontSizeMultiplier;
  const buttonFontSize = 0.75 * fontSizeMultiplier;

  return (
    <Card
      ref={cardRef}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      elevation={1}
      sx={{
        height: cardHeight,
        display: 'flex',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        cursor: 'pointer',
        transition: theme.transitions.create(['transform', 'box-shadow', 'border-color']),
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[8],
          borderColor: 'primary.light',
          backgroundColor: 'background.paper',
          '& .card-content': {
            backgroundColor: theme.palette.action.hover
          }
        }
      }}
    >
      {/* Profile ID Badge - Only shows on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: 8 * paddingMultiplier,
          right: 8 * paddingMultiplier,
          padding: `${2 * paddingMultiplier}px ${8 * paddingMultiplier}px`,
          borderRadius: '12px',
          fontSize: `${0.7 * fontSizeMultiplier}rem`,
          fontWeight: 600,
          zIndex: 20,
          backgroundColor: isFemale ? 'primary.main' : 'background.paper',
          color: isFemale ? 'primary.contrastText' : 'primary.main',
          border: isFemale ? 'none' : `${1.5 * fontSizeMultiplier}px solid`,
          borderColor: 'primary.main',
          opacity: isHovered ? 1 : 0,
          visibility: isHovered ? 'visible' : 'hidden',
          transform: isHovered ? 'scale(1)' : 'scale(0.9)',
          transition: theme.transitions.create(['opacity', 'visibility', 'transform'], {
            duration: 200,
            easing: 'ease-in-out'
          }),
          pointerEvents: 'none',
          boxShadow: isHovered ? theme.shadows[2] : 'none'
        }}
      >
        {profileId}
      </Box>

      {/* Image Container */}
      <Box sx={{
        width: imageContainerWidth,
        minWidth: imageContainerWidth,
        height: '100%',
        position: 'relative',
        flexShrink: 0,
        p: 1 * paddingMultiplier,
      }}>
        <Box sx={{
          width: imageWidth,
          height: imageHeight,
          borderRadius: `${4 * paddingMultiplier}px`,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'grey.100'
        }}>
          {/* Placeholder/Skeleton */}
          {!isInView && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
            />
          )}

          {/* Image */}
          {isInView && finalImageUrl && (
            <Box
              component="img"
              ref={imgRef}
              src={finalImageUrl}
              alt={profile.full_name}
              onError={handleImageError}
              onLoad={handleImageLoad}
              loading={priority ? 'eager' : 'lazy'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
                transition: theme.transitions.create('opacity'),
                opacity: imageLoaded ? 1 : 0,
                backgroundColor: imageError ? 
                  (isFemale ? 'rgba(236, 64, 122, 0.1)' : 'rgba(25, 118, 210, 0.1)') : 
                  'grey.100'
              }}
            />
          )}

          {/* Verified Badge */}
          {profile.verified?.cnic && (
            <Box
              sx={{
                position: 'absolute',
                top: 4 * paddingMultiplier,
                left: 4 * paddingMultiplier,
                width: 18 * fontSizeMultiplier,
                height: 18 * fontSizeMultiplier,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'success.contrastText',
                zIndex: 2,
                boxShadow: theme.shadows[2],
                border: '1px solid',
                borderColor: 'background.paper'
              }}
            >
              <VerifiedIcon sx={{ fontSize: `${0.7 * fontSizeMultiplier}rem` }} />
            </Box>
          )}
          
          {/* Zodiac Badge */}
          {zodiac.sign !== 'Unknown' && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4 * paddingMultiplier,
                left: 4 * paddingMultiplier,
                width: 32 * fontSizeMultiplier,
                height: 32 * fontSizeMultiplier,
                borderRadius: '50%',
                bgcolor: zodiacColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: `${1.0 * fontSizeMultiplier}rem`,
                fontFamily: '"Segoe UI Symbol", "Apple Symbols", "Noto Sans Symbols", "Symbola", "Arial Unicode MS", sans-serif',
                zIndex: 2,
                boxShadow: theme.shadows[2]
              }}
            >
              {zodiac.symbol}
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Content Area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: 2 * paddingMultiplier,
        overflow: 'hidden'
      }}>
        {/* Name, Age, Gender */}
        <Box sx={{ mb: 1 * paddingMultiplier }}>
          <Typography
            variant="whizText1"
            sx={{
              fontWeight: 700,
              fontSize: `${nameFontSize}rem`,
              lineHeight: 1.2,
              mb: 0.5 * paddingMultiplier,
            }}
          >
            {profile.full_name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 * paddingMultiplier }}>
            <Typography variant="whizText2" sx={{
              fontWeight: 600,
              fontSize: `${captionFontSize}rem`
            }}>
              {profile.age || '--'}y
            </Typography>
            <Typography variant="whizText2" sx={{ fontSize: `${captionFontSize}rem` }}>•</Typography>
            <Typography variant="whizText2" sx={{ 
              fontWeight: 600,
              fontSize: `${captionFontSize}rem`
            }}>
              {genderDisplay}
            </Typography>
          </Box>
        </Box>

        {/* Quick Info */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {profile.city && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 * paddingMultiplier }}>
              <LocationOnIcon sx={{ 
                color: 'primary.main', 
                mr: 0.5 * paddingMultiplier, 
                fontSize: `${captionFontSize}rem` 
              }} />
              <Typography variant="whizText2" sx={{
                fontSize: `${captionFontSize}rem`
              }}>
                {profile.city}
              </Typography>
            </Box>
          )}

          {profile.job_title && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 * paddingMultiplier }}>
              <WorkIcon sx={{ 
                color: 'primary.main', 
                mr: 0.5 * paddingMultiplier, 
                fontSize: `${captionFontSize}rem` 
              }} />
              <Typography variant="whizText2" sx={{
                fontSize: `${captionFontSize}rem`
              }}>
                {profile.job_title}
              </Typography>
            </Box>
          )}

          {profile.highest_degree && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 * paddingMultiplier }}>
              <SchoolIcon sx={{ 
                color: 'primary.main', 
                mr: 0.5 * paddingMultiplier, 
                fontSize: `${captionFontSize}rem` 
              }} />
              <Typography variant="whizText2" sx={{
                fontSize: `${captionFontSize}rem`
              }}>
                {profile.highest_degree}
              </Typography>
            </Box>
          )}

          {formattedIncome && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 * paddingMultiplier }}>
              <MonetizationOnIcon sx={{ 
                color: 'success.main', 
                mr: 0.5 * paddingMultiplier, 
                fontSize: `${captionFontSize}rem` 
              }} />
              <Typography variant="whizText2" sx={{
                fontSize: `${captionFontSize}rem`,
                color: 'success.main', 
                fontWeight: 600 
              }}>
                {formattedIncome}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tags */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: `${4 * fontSizeMultiplier}px`,
          mb: `${8 * paddingMultiplier}px`
        }}>
          {profile.religion && (
            <Typography variant="whizText2" sx={{
              fontWeight: 500,
              fontSize: `${0.8 * fontSizeMultiplier}rem`,
            }}>
              {profile.religion}
            </Typography>
          )}

          {profile.marital_status && (
            <>
              <Typography variant="whizText2" sx={{ fontSize: `${0.8 * fontSizeMultiplier}rem` }}>•</Typography>
              <Typography variant="whizText2" sx={{
                fontWeight: 500,
                fontSize: `${0.8 * fontSizeMultiplier}rem`,
              }}>
                {profile.marital_status}
              </Typography>
            </>
          )}

          {profile.hobbies && profile.hobbies.length > 0 && (
            <>
              <Typography variant="whizText2" sx={{ fontSize: `${0.8 * fontSizeMultiplier}rem` }}>•</Typography>
              <Typography variant="whizText2" sx={{
                fontWeight: 500,
                fontSize: `${0.8 * fontSizeMultiplier}rem`,
              }}>
                {profile.hobbies[0]}
              </Typography>
            </>
          )}
        </Box>

        {/* Action Buttons */}
        <Stack 
          direction="row" 
          spacing={1 * paddingMultiplier}
        >
          <Tooltip title="View Profile">
            <Button
              variant="outlined"
              size="small"
              onClick={handleViewClick}
              startIcon={<VisibilityIcon sx={{ fontSize: `${0.9 * fontSizeMultiplier}rem` }} />}
              sx={{
                flex: 1,
                fontSize: `${buttonFontSize}rem`,
                py: 0.5 * paddingMultiplier,
                minHeight: 'auto',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              View
            </Button>
          </Tooltip>

          <Tooltip title="Share via WhatsApp">
            <Button
              variant="outlined"
              size="small"
              onClick={handleShareClick}
              startIcon={<ShareIcon sx={{ fontSize: `${0.9 * fontSizeMultiplier}rem` }} />}
              sx={{
                flex: 1,
                fontSize: `${buttonFontSize}rem`,
                py: 0.5 * paddingMultiplier,
                minHeight: 'auto',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              Share
            </Button>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};

export default React.memo(ProfileCard);